import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Card, ErrorBanner, Input, Spinner } from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useChangePassword } from "@/lib/queries";

/**
 * Trava do primeiro acesso com senha provisória.
 *
 * Quem chega aqui recebeu a senha do dono, escrita num papel ou dita em
 * voz alta. Deixar passar direto significaria que o dono continua sabendo
 * a senha de quem recebe repasse — e é essa conta que edita a chave Pix.
 * Por isso não há botão de "depois": o Layout redireciona para cá
 * enquanto `must_change_password` for verdadeiro.
 *
 * Não pede a senha atual: ele acabou de digitá-la no login e a sessão
 * já prova que a sabe. O backend aplica a mesma regra.
 */
export default function NewPassword() {
  const t = useT();
  const trocar = useChangePassword();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");

  const erro = trocar.isError ? toApiError(trocar.error) : null;
  const naoConfere = confirma.length > 0 && nova !== confirma;

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    if (naoConfere) return;
    trocar.mutate(
      { new_password: nova },
      {
        onSuccess: () => {
          // O /me/ carrega must_change_password; sem invalidar, o Layout
          // continuaria devolvendo o usuário para esta tela.
          qc.invalidateQueries({ queryKey: ["me"] });
          navigate("/", { replace: true });
        },
      },
    );
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-carbon-900 p-4">
      <Card className="w-full max-w-sm p-6">
        <KeyRound className="mx-auto mb-3 text-racing-500" size={32} />
        <h1 className="text-center font-display text-xl font-bold">{t("auth.newPasswordTitle")}</h1>
        <p className="mt-2 text-center text-sm text-steel-muted">
          {t("auth.tempPasswordHint")}
        </p>

        <form onSubmit={enviar} className="mt-6 space-y-4">
          <Input
            label={t("auth.newPassword")}
            type="password"
            autoComplete="new-password"
            required
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            hint={t("auth.minChars")}
            error={erro?.field === "new_password" ? erro.message : undefined}
          />
          <Input
            label={t("modal.repeatNewPassword")}
            type="password"
            autoComplete="new-password"
            required
            value={confirma}
            onChange={(e) => setConfirma(e.target.value)}
            error={naoConfere ? t("modal.passwordsDiffer") : undefined}
          />

          {erro && !erro.field && <ErrorBanner message={erro.message} />}

          <Button type="submit" className="w-full" disabled={trocar.isPending || naoConfere}>
            {trocar.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              t("modal.saveAndEnter")
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
